<!--
  =====================================================================
  SECTION ACTIVITY CARD - ACTIVITY CATEGORY CARD
  =====================================================================
  
  Simple card component for displaying activity category.
  
  FEATURES:
  - Fixed height card layout
  - Vertical bar animation on hover
  - Title and description text
  - Click navigation to activity details
  - Responsive sizing
  
  BEHAVIOR:
  - Card shrinks/lifts on hover
  - Vertical bar animates from middle outward
  - Click anywhere on card navigates to activity link
  
  =====================================================================
-->

<template>
  <div class="activity-card" @click="goToActivity">
    
    <div class="vertical-bar"></div>

    <div class="card-content">
      <h2 class="card-title">{{ card.label }}</h2>
      <p class="card-text">{{ card.text }}</p>
    </div>
  </div>
</template>

<script setup>
// ───────────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Object} card - Card data with label, text, link properties
 */
const props = defineProps({
  card: {
    type: Object,
    required: true
  }
})

// ───────────────────────────────────────────────────────────────────
// NAVIGATION HANDLER
// ───────────────────────────────────────────────────────────────────

/**
 * Navigates to activity category page
 */
function goToActivity() {
  window.location.href = props.card.link
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";

.activity-card {
  position: relative;
  display: flex;
  background: $surface-base;
  color: $text-color;
  cursor: pointer;
  padding: $space-2 $space-2-5;
  height: 140px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 4px rgba($clr-brand-dark, 0.05);
  border: 1px solid $clr-border;

  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba($clr-brand-dark, 0.12);
      border-color: rgba($clr-brand-dark, 0.2);
    }

    &:hover .vertical-bar::before,
    &:hover .vertical-bar::after {
      height: 50%;
      background: $clr-brand-dark;    
    }
  }

  /* Animated vertical bar */
  .vertical-bar {
    position: absolute;
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    pointer-events: none;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 0;
      width: 3px;
      background: $clr-border;       
      height: 0;                   
      transition: height 0.5s ease, background 0.5s ease;
    }

    &::before {
      bottom: 50%;
    }
    &::after {
      top: 50%;
    }
  }

  .card-content {
    margin-left: $space-1-25;  
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .card-title {
    font-size: clamp(1.4rem, 2.5vw, 1.8rem);
    font-weight: 500;
    line-height: 1.2;
    font-family: 'Karla', sans-serif;
  }

  .card-text {
    font-size: clamp(0.95rem, 1.5vw, 1.1rem);
    color: $clr-text-light;
    margin: 0;
    line-height: 1.5;
  }
}

@media (max-width: 1300px) {
  .activity-card {
    padding: $space-1-75 $space-2;
    height: 130px;

    .card-title {
      font-size: clamp(1.3rem, 2.3vw, 1.6rem);
      margin-bottom: $space-0-5;
    }

    .card-text {
      font-size: clamp(0.9rem, 1.4vw, 1.05rem);
    }
  }
}

@media (max-width: 920px) {
  .activity-card {
    padding: $space-1-5 $space-1-75;
    height: 120px;

    .card-content {
      margin-left: $space-1;
    }

    .card-title {
      font-size: clamp(1.2rem, 2.2vw, 1.5rem);
    }

    .card-text {
      font-size: clamp(0.85rem, 1.3vw, 1rem);
    }
  }
}

@media (max-width: 480px) {
  .activity-card {
    padding: $space-1-25 $space-1-5;
    height: 110px;

    .card-content {
      margin-left: $space-0-75;
    }

    .card-title {
      font-size: clamp(1.1rem, 3vw, 1.3rem);
      margin-bottom: $space-0-25;
    }

    .card-text {
      font-size: clamp(0.8rem, 2.5vw, 0.95rem);
      line-height: 1.4;
    }
  }
}

@media (max-width: 360px) {
  .activity-card {
    padding: $space-1 $space-1-25;
    height: 100px;

    .card-title {
      font-size: clamp(1rem, 3.5vw, 1.2rem);
    }

    .card-text {
      font-size: clamp(0.75rem, 2.8vw, 0.9rem);
    }
  }
}
</style>